import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown, ChevronRight } from "lucide-react";

const CollapsibleSection = ({ title, defaultOpen = false, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    const Icon = open ? ChevronDown : ChevronRight;

    return (
        <div className="border rounded-lg bg-white">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center gap-2 p-3 text-left hover:bg-slate-50 font-medium text-sm"
            >
                <Icon className="h-4 w-4 text-slate-400" />
                {title}
            </button>
            {open && <div className="border-t p-3">{children}</div>}
        </div>
    );
};

export default function DebugTab({ data }) {
    const { t } = useTranslation();
    const debug = data?.agentResult?.debug || data?.debug || {};
    const rawResult = data?.agentResult || data?.result;
    const trace = debug.toolCallTrace || [];

    return (
        <div className="space-y-3 mt-3">
            <div className="grid grid-cols-3 gap-3">
                <div className="bg-white border rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold">{debug.duration ? `${(debug.duration / 1000).toFixed(1)}s` : "-"}</p>
                    <p className="text-xs text-slate-500">{t("admin.automationPlayground.duration")}</p>
                </div>
                <div className="bg-white border rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold">{debug.iterationCount ?? "-"}</p>
                    <p className="text-xs text-slate-500">{t("admin.automationPlayground.iterations")}</p>
                </div>
                <div className="bg-white border rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold">{trace.length}</p>
                    <p className="text-xs text-slate-500">{t("admin.automationPlayground.toolCallsCount")}</p>
                </div>
            </div>
            <CollapsibleSection title={t("admin.automationPlayground.toolCallTrace")} defaultOpen>
                <div className="space-y-2 max-h-[500px] overflow-auto">
                    {trace.map((tc, i) => (
                        <div key={i} className="bg-slate-50 rounded p-2 text-xs font-mono">
                            <span className="text-slate-400">{tc.timestamp}</span>
                            <pre className="whitespace-pre-wrap mt-1">{JSON.stringify(tc, null, 2)}</pre>
                        </div>
                    ))}
                </div>
            </CollapsibleSection>
            {debug.systemPrompt && (
                <CollapsibleSection title={t("admin.automationPlayground.systemPrompt")}>
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-slate-900 text-slate-200 p-3 rounded max-h-[500px] overflow-auto">
                        {debug.systemPrompt}
                    </pre>
                </CollapsibleSection>
            )}
            {debug.workflowPattern && (
                <CollapsibleSection title={t("admin.automationPlayground.workflowPattern")}>
                    <pre className="text-xs font-mono whitespace-pre-wrap bg-slate-50 p-3 rounded">
                        {JSON.stringify(debug.workflowPattern, null, 2)}
                    </pre>
                </CollapsibleSection>
            )}
            <CollapsibleSection title={t("admin.automationPlayground.rawResult")}>
                <pre className="text-xs font-mono whitespace-pre-wrap bg-slate-50 p-3 rounded max-h-[500px] overflow-auto">
                    {JSON.stringify(rawResult, null, 2)}
                </pre>
            </CollapsibleSection>
        </div>
    );
}
